// src/Layouts/GateEntry/parts/useGateSocket.ts
//
// Live gate movements from the Node socket (namespace /gate). Every IN / OUT
// recorded at any gate or desk of this school arrives here within a moment,
// so the dashboard no longer polls every 30 s while connected.
//
//   Socket address: SOCKET_BASE_URL in apiSetup/apiConfig.ts (per environment,
//   like API_BASE_URL). Empty → no socket, the dashboard just polls.
//
// Auth: the socket needs the same token the API calls send. Instead of
// guessing where it's stored, this runs apiClient's own request interceptors
// on a dummy request and takes the Authorization header (and school_id param,
// if the interceptor adds one) they produce. Every reconnect asks again, so a
// refreshed token is picked up automatically.
//
// Reconnects:
//   • network drop          → socket.io retries by itself
//   • server dropped us     → token expired (1 h); retry with a fresh token
//   • auth refused          → one API call (onResync) lets the axios refresh
//                             flow renew the token, then retry with backoff
// After every (re)connect onResync runs once, to pick up anything missed.

import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { AxiosHeaders } from 'axios';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { SOCKET_BASE_URL } from '../../../apiSetup/apiConfig';

export type GateSocketStatus = 'off' | 'connecting' | 'live' | 'offline';

// One row of the gate:movements event (see socket/gateMovementStream.js).
export interface SocketMovement {
  movement_id: string;
  school_id: string;
  student_id: string;
  name: string;
  sp_student_id: string;
  class_id: string | null;
  class_name: string;
  section: string;
  roll_no: number | null;
  direction: 'IN' | 'OUT';
  type: string;
  gate: string;
  method: string;
  date: string; // IST YYYY-MM-DD
  timestamp: string;
}

const SOCKET_URL: string = (SOCKET_BASE_URL || '').replace(/\/+$/, '');
const RETRY_MIN_MS = 3_000;
const RETRY_MAX_MS = 60_000;

// The token (and school_id for superadmin) exactly as apiClient would send them.
async function apiAuth(): Promise<{ token: string; school_id?: string }> {
  let config: any = { url: 'api/movements/live', method: 'get', headers: new AxiosHeaders(), params: {} };
  // axios runs request interceptors last-registered first.
  const handlers: any[] = [...((apiClient.interceptors.request as any).handlers || [])].reverse();
  for (const h of handlers) {
    if (h?.fulfilled) config = (await h.fulfilled(config)) || config;
  }
  const headers = config.headers;
  const defaults: any = (apiClient as any).defaults?.headers;
  const header =
    (typeof headers?.get === 'function' ? headers.get('Authorization') : undefined) ??
    headers?.Authorization ??
    headers?.authorization ??
    // token set once on the instance instead of in an interceptor
    defaults?.common?.Authorization ??
    defaults?.Authorization ??
    '';
  const auth: { token: string; school_id?: string } = {
    token: String(header || '').replace(/^Bearer\s+/i, ''),
  };
  if (config.params?.school_id) auth.school_id = String(config.params.school_id);
  return auth;
}

// ── One shared socket ───────────────────────────────────────────────────────
// The dashboard can unmount and mount again quickly (StrictMode in dev, a
// permission guard or layout re-rendering in the app). Each of those used to
// open a fresh socket and close the old one mid-handshake ("closed before the
// connection is established"). Now there is one socket for the page: a remount
// within CLOSE_DELAY_MS reuses it, and it only closes after the screen is left.

const CLOSE_DELAY_MS = 2_000;

interface Listener {
  onStatus: (s: GateSocketStatus) => void;
  onMovements: (rows: SocketMovement[]) => void;
  onResync: () => void;
}

interface Shared {
  socket: Socket;
  status: GateSocketStatus;
  listeners: Set<Listener>;
  retryMs: number;
  retryTimer?: ReturnType<typeof setTimeout>;
  closeTimer?: ReturnType<typeof setTimeout>;
}

let shared: Shared | null = null;

function acquireSocket(): Shared {
  if (shared) {
    clearTimeout(shared.closeTimer); // a remount — keep the same connection
    return shared;
  }

  const socket: Socket = io(`${SOCKET_URL}/gate`, {
    transports: ['websocket'],
    auth: (send) => {
      apiAuth()
        .then((a) => {
          console.log('[GateSocket] auth: token', a.token ? `present (${a.token.length} chars)` : 'MISSING', '| school_id:', a.school_id || '-');
          send(a);
        })
        .catch((e) => {
          console.warn('[GateSocket] auth: could not read token —', e?.message || e);
          send({ token: '' });
        });
    },
  });
  // Low-level connection events (before the /gate namespace login)
  socket.io.on('open', () => console.log('[GateSocket] transport open →', SOCKET_URL));
  socket.io.on('close', (reason) => console.warn('[GateSocket] transport closed:', reason));
  socket.io.on('error', (err) => console.warn('[GateSocket] transport error:', err?.message || err));

  const s: Shared = { socket, status: 'connecting', listeners: new Set(), retryMs: RETRY_MIN_MS };

  const setStatus = (status: GateSocketStatus) => {
    s.status = status;
    s.listeners.forEach((l) => l.onStatus(status));
  };
  const resync = () => s.listeners.forEach((l) => l.onResync());
  const retryLater = () => {
    clearTimeout(s.retryTimer);
    s.retryTimer = setTimeout(() => socket.connect(), s.retryMs);
    s.retryMs = Math.min(s.retryMs * 2, RETRY_MAX_MS);
  };

  socket.on('gate:ready', (info) => {
    console.log('[GateSocket] live:', info);
    s.retryMs = RETRY_MIN_MS;
    setStatus('live');
    resync(); // catch up on anything missed while disconnected
  });

  socket.on('gate:movements', (rows: SocketMovement[]) => {
    if (Array.isArray(rows) && rows.length) s.listeners.forEach((l) => l.onMovements(rows));
  });

  socket.on('disconnect', (reason) => {
    console.warn('[GateSocket] disconnect:', reason);
    setStatus('offline');
    // Dropped by the server (token expired) — socket.io won't retry this one itself.
    if (reason === 'io server disconnect') retryLater();
  });

  socket.on('connect_error', (err) => {
    console.warn('[GateSocket] connect_error:', err.message, '| active:', socket.active);
    setStatus('offline');
    // Refused by auth (socket.active === false): let an API call refresh the
    // token, then try again. Network errors are retried by socket.io itself.
    if (!socket.active) {
      resync();
      retryLater();
    }
  });

  shared = s;
  return s;
}

function releaseSocket(s: Shared) {
  if (s.listeners.size > 0) return;
  clearTimeout(s.closeTimer);
  s.closeTimer = setTimeout(() => {
    if (s.listeners.size > 0) return;
    clearTimeout(s.retryTimer);
    s.socket.removeAllListeners();
    s.socket.disconnect();
    if (shared === s) shared = null;
  }, CLOSE_DELAY_MS);
}

export function useGateSocket(
  onMovements: (rows: SocketMovement[]) => void,
  onResync: () => void,
): GateSocketStatus {
  const [status, setStatus] = useState<GateSocketStatus>(SOCKET_URL ? 'connecting' : 'off');
  // Latest callbacks without reconnecting when they change.
  const cb = useRef({ onMovements, onResync });
  cb.current = { onMovements, onResync };

  useEffect(() => {
    if (!SOCKET_URL) return; // no socket configured → dashboard keeps polling

    const s = acquireSocket();
    const listener: Listener = {
      onStatus: setStatus,
      onMovements: (rows) => cb.current.onMovements(rows),
      onResync: () => cb.current.onResync(),
    };
    s.listeners.add(listener);
    setStatus(s.status); // reused socket may already be live

    return () => {
      s.listeners.delete(listener);
      releaseSocket(s);
    };
  }, []);

  return status;
}